import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import type { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { FIELD_PROBABILITY } from '@/modules/planner/constants/assignment-modal-constants';
import {
  DEFAULT_ASSIGNMENT_PROBABILITY,
  getDefaultAssignmentProbability,
  isAssignmentProbabilityVisible,
  normalizeProjectProbability,
} from '@/modules/planner/helpers/assignment/probability';
import { getProjectDetails } from '@/modules/projects';
import { ProjectStatus } from '@/modules/projects/types/project';

import type {
  AssignmentFormValues,
  AssignmentModalProps,
} from '../../components/modals/assignment-modal/assignment-modal';
import { AssignmentStatus } from '../../types/assignment-status';

interface UseAssignmentProbabilityStateParams {
  open: boolean;
  isEditMode: boolean;
  initialData: AssignmentModalProps['initialData'];
  projectId: string;
  selectedProject: DropdownSearchItem | null;
  status: AssignmentStatus;
  probability: number | null;
  getValues: UseFormGetValues<AssignmentFormValues>;
  setValue: UseFormSetValue<AssignmentFormValues>;
}

interface AssignmentProbabilityUiStateParams {
  initialData: AssignmentModalProps['initialData'];
  projectId: string;
  projectDetailsProbability: number | null;
  status: AssignmentStatus;
  probability: number | null;
}

const UPDATE_PROBABILITY_OPTIONS = { shouldDirty: true, shouldValidate: true };

function normalizeProjectId(projectId: string | number | undefined): string {
  return projectId === undefined ? '' : String(projectId);
}

function isProbabilityMissing(probability: number | null): boolean {
  return probability === null || probability === undefined;
}

function shouldKeepExistingProbabilityEmpty({
  isEditMode,
  initialData,
}: {
  isEditMode: boolean;
  initialData: AssignmentModalProps['initialData'];
}): boolean {
  // Existing assignments can have visible probability with no saved value; keep the field empty.
  return (
    isEditMode &&
    initialData?.probability == null &&
    isAssignmentProbabilityVisible(initialData?.status ?? AssignmentStatus.Confirmed)
  );
}

function getProjectDetailsProbability(
  projectDetails: Awaited<ReturnType<typeof getProjectDetails>> | undefined
): number | null {
  // The project dropdown item is intentionally short and does not include tentativeProbability.
  return normalizeProjectProbability(projectDetails?.project.info.tentativeProbability);
}

function getInitialProjectProbability({
  initialData,
  projectId,
}: {
  initialData: AssignmentModalProps['initialData'];
  projectId: string;
}): number | null {
  if (normalizeProjectId(initialData?.projectId) !== projectId) return null;

  return normalizeProjectProbability(initialData?.projectProbability);
}

function getCurrentProjectProbability({
  initialData,
  projectId,
  projectDetailsProbability,
}: Pick<
  AssignmentProbabilityUiStateParams,
  'initialData' | 'projectId' | 'projectDetailsProbability'
>): number | null {
  // Priority mirrors freshness: fetched project details, then the lightweight
  // initial assignment data used before the details query resolves.
  return projectDetailsProbability ?? getInitialProjectProbability({ initialData, projectId });
}

function getAssignmentProbabilityUiState({
  initialData,
  projectId,
  projectDetailsProbability,
  status,
  probability,
}: AssignmentProbabilityUiStateParams) {
  const shouldShowProbability = isAssignmentProbabilityVisible(status);
  const currentProjectProbability = getCurrentProjectProbability({
    initialData,
    projectId,
    projectDetailsProbability,
  });
  const isMissing = isProbabilityMissing(probability);

  return {
    currentProjectProbability,
    shouldShowProbability,
    shouldShowProbabilityMismatchAlert:
      currentProjectProbability !== null &&
      shouldShowProbability &&
      !isMissing &&
      probability !== currentProjectProbability,
  };
}

export function useAssignmentProbabilityState({
  open,
  isEditMode,
  initialData,
  projectId,
  selectedProject,
  status,
  probability,
  getValues,
  setValue,
}: UseAssignmentProbabilityStateParams) {
  const initialProjectId = normalizeProjectId(initialData?.projectId);
  const numericProjectId = Number(projectId);
  const shouldLoadProjectDetails =
    open && Number.isFinite(numericProjectId) && numericProjectId > 0;

  const { data: selectedProjectDetails } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectDetails(numericProjectId),
    enabled: shouldLoadProjectDetails,
  });

  const { currentProjectProbability, shouldShowProbability, shouldShowProbabilityMismatchAlert } =
    getAssignmentProbabilityUiState({
      initialData,
      projectId,
      projectDetailsProbability: getProjectDetailsProbability(selectedProjectDetails),
      status,
      probability,
    });
  const shouldKeepExistingEmpty = shouldKeepExistingProbabilityEmpty({
    isEditMode,
    initialData,
  });
  // Wait for details so tentative projects use their own probability instead of the generic default.
  const shouldWaitForSelectedProjectProbability =
    selectedProject?.status === ProjectStatus.Tentative && currentProjectProbability === null;

  const wasOpenRef = useRef(false);
  const handledProjectIdRef = useRef(initialProjectId);
  const wasProbabilityVisibleRef = useRef(shouldShowProbability);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // The selector calls onSelect for initialSelectedItem, so the initial
      // project must already be marked handled on every fresh modal open.
      handledProjectIdRef.current = initialProjectId;
      wasProbabilityVisibleRef.current = isAssignmentProbabilityVisible(
        initialData?.status ?? AssignmentStatus.Confirmed
      );
    }

    wasOpenRef.current = open;
  }, [initialData?.status, initialProjectId, open]);

  useEffect(() => {
    if (!open) return;

    const isMissing = isProbabilityMissing(probability);
    const wasProbabilityVisible = wasProbabilityVisibleRef.current;
    wasProbabilityVisibleRef.current = shouldShowProbability;

    if (!shouldShowProbability) {
      if (!isMissing) {
        setValue(FIELD_PROBABILITY, null, UPDATE_PROBABILITY_OPTIONS);
      }
      return;
    }

    if (
      !wasProbabilityVisible &&
      isMissing &&
      !shouldKeepExistingEmpty &&
      !shouldWaitForSelectedProjectProbability
    ) {
      setValue(
        FIELD_PROBABILITY,
        getDefaultAssignmentProbability(currentProjectProbability),
        UPDATE_PROBABILITY_OPTIONS
      );
    }
  }, [
    currentProjectProbability,
    open,
    probability,
    setValue,
    shouldKeepExistingEmpty,
    shouldShowProbability,
    shouldWaitForSelectedProjectProbability,
  ]);

  useEffect(() => {
    if (!open || !selectedProject) return;

    const isInitialProject = selectedProject.id === initialProjectId;
    const isProjectAlreadyHandled = selectedProject.id === handledProjectIdRef.current;

    if (shouldKeepExistingEmpty && isInitialProject) {
      // Initial selector hydration must not turn an existing empty value into the default probability.
      handledProjectIdRef.current = selectedProject.id;
      return;
    }

    if (selectedProject.status === ProjectStatus.Tentative) {
      if (currentProjectProbability === null) return;
      if (isProjectAlreadyHandled) return;

      handledProjectIdRef.current = selectedProject.id;
      setValue(
        FIELD_PROBABILITY,
        getDefaultAssignmentProbability(currentProjectProbability),
        UPDATE_PROBABILITY_OPTIONS
      );
      return;
    }

    if (isProjectAlreadyHandled) return;

    handledProjectIdRef.current = selectedProject.id;

    const currentProbability = getValues(FIELD_PROBABILITY);
    if (shouldShowProbability && isProbabilityMissing(currentProbability)) {
      setValue(FIELD_PROBABILITY, DEFAULT_ASSIGNMENT_PROBABILITY, UPDATE_PROBABILITY_OPTIONS);
    }
  }, [
    getValues,
    initialProjectId,
    open,
    currentProjectProbability,
    selectedProject,
    setValue,
    shouldKeepExistingEmpty,
    shouldShowProbability,
  ]);

  return {
    currentProjectProbability,
    currentProjectStatus: selectedProjectDetails?.project.info.status,
    shouldShowProbability,
    shouldShowProbabilityMismatchAlert,
  };
}
